<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import ConfirmButton from 'dashboard/components-next/button/ConfirmButton.vue';

const props = defineProps({
  listing: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();

const ALLOWED_TYPES = ['video/mp4', 'video/quicktime', 'video/webm'];
const SIZE_LIMIT = 100 * 1024 * 1024;

const uiFlags = useMapGetter('wgptCarListings/getUIFlags');
const isDragging = ref(false);

const video = computed(() => props.listing.video);
const isUploading = computed(() => uiFlags.value.isUploadingVideo);

const videoSizeLabel = computed(() => {
  if (!video.value?.byte_size) return '';
  return `${(video.value.byte_size / (1024 * 1024)).toFixed(1)} MB`;
});

const errorMessageFor = error => {
  if (error?.message === 'FILE_SIZE_ERROR')
    return t('WGPT_CAR_LISTINGS.EDITOR.VIDEO.ERRORS.SIZE');
  if (error?.message === 'INVALID_VIDEO_FORMAT')
    return t('WGPT_CAR_LISTINGS.EDITOR.VIDEO.ERRORS.FORMAT');
  return error?.message || t('WGPT_CAR_LISTINGS.EDITOR.VIDEO.ERRORS.GENERIC');
};

const uploadVideo = async file => {
  if (!file) return;
  if (!ALLOWED_TYPES.includes(file.type)) {
    useAlert(t('WGPT_CAR_LISTINGS.EDITOR.VIDEO.ERRORS.FORMAT'));
    return;
  }
  if (file.size > SIZE_LIMIT) {
    useAlert(t('WGPT_CAR_LISTINGS.EDITOR.VIDEO.ERRORS.SIZE'));
    return;
  }
  try {
    await store.dispatch('wgptCarListings/attachVideo', {
      id: props.listing.id,
      file,
    });
    useAlert(t('WGPT_CAR_LISTINGS.EDITOR.VIDEO.UPLOAD_SUCCESS'));
  } catch (error) {
    useAlert(errorMessageFor(error));
  }
};

const handleFileChange = event => {
  uploadVideo(event.target.files[0]);
  event.target.value = '';
};

const handleDrop = event => {
  isDragging.value = false;
  uploadVideo(event.dataTransfer.files[0]);
};

const handleRemove = async () => {
  try {
    await store.dispatch('wgptCarListings/removeVideo', {
      id: props.listing.id,
    });
  } catch (error) {
    useAlert(errorMessageFor(error));
  }
};
</script>

<template>
  <div class="flex flex-col gap-2">
    <div v-if="video" class="flex flex-col max-w-xl gap-2">
      <video
        :src="video.file_url"
        controls
        class="w-full overflow-hidden border rounded-xl border-n-weak bg-n-alpha-1"
      />
      <div class="flex items-center justify-between">
        <span class="text-xs truncate text-n-slate-11">
          {{ video.filename }}
          <template v-if="videoSizeLabel"> · {{ videoSizeLabel }}</template>
        </span>
        <ConfirmButton
          :confirm-label="t('WGPT_CAR_LISTINGS.EDITOR.VIDEO.REMOVE')"
          icon="i-lucide-trash-2"
          variant="ghost"
          color="slate"
          size="sm"
          @click="handleRemove"
        />
      </div>
    </div>

    <label
      v-else
      class="flex flex-col items-center justify-center max-w-xl gap-2 p-8 transition-colors border-2 border-dashed cursor-pointer rounded-xl text-n-slate-10 hover:text-n-slate-12 hover:border-n-slate-7"
      :class="isDragging ? 'border-n-blue-8 bg-n-blue-2' : 'border-n-weak'"
      @dragover.prevent="isDragging = true"
      @dragleave.prevent="isDragging = false"
      @drop.prevent="handleDrop"
    >
      <span
        class="block text-2xl"
        :class="
          isUploading
            ? 'i-lucide-loader-circle animate-spin'
            : 'i-lucide-clapperboard'
        "
      />
      <span class="text-sm font-medium">
        {{
          isUploading
            ? t('WGPT_CAR_LISTINGS.EDITOR.VIDEO.UPLOADING')
            : t('WGPT_CAR_LISTINGS.EDITOR.VIDEO.UPLOAD')
        }}
      </span>
      <span class="text-xs text-center">
        {{ t('WGPT_CAR_LISTINGS.EDITOR.VIDEO.HINT') }}
      </span>
      <input
        type="file"
        accept="video/mp4,video/quicktime,video/webm"
        class="hidden"
        :disabled="isUploading"
        @change="handleFileChange"
      />
    </label>
  </div>
</template>
